import React, { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid2 as Grid,
  Paper,
  styled,
} from "@mui/material";
import { convertToBase64 } from "../utils/helpers";
import { Link } from "lucide-react";
import "./welcomePage.css";
import MosierLogo from "../utils/images/mosier-logo.png";

const GradientBackground = styled(Box)({
  background: "white",
  padding: "2rem",
  color: "black",
});

const DeviceFrame = styled(Paper)(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, 0.9)",
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  position: "relative",
}));

function WelcomePage({ images, domain, description, generatedBy }) {
  useEffect(() => {
    const fetchImages = async () => {
      await convertToBase64(images.desktop);
      await convertToBase64(images.mobile);
    };

    fetchImages();
  }, [images]);

  return (
    <GradientBackground>
      <Container
        maxWidth="lg"
        sx={{
          padding: { xs: 0, md: "1rem" },
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            mb: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: "bold",
              mb: 2,
              fontSize: { xs: "2rem", sm: "3rem" },
            }}
          >
            WEBSITE AUDIT FOR
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              border: "1px solid rgb(17, 17, 17)",
              boxShadow: "rgb(17, 17, 19) -4px 4px 0px 0px",
              borderRadius: "25px",
              backgroundColor: "rgb(7, 135, 167)",
              cursor: "pointer",
              width: "fit-content",
              padding: { xs: "0.1rem 0.5rem", sm: "0.1rem 2rem" },
              "&:hover": { backgroundColor: "rgba(7, 135, 167, 0.9)" },
            }}
            onClick={() => {
              window.open(domain, "_blank");
            }}
          >
            <Link size={24} color="white" />
            <Typography
              variant="h3"
              sx={{
                color: "white",
                fontWeight: 500,
                fontSize: { xs: "18px", sm: "28px" },
                lineHeight: 1.4,
                fontFamily: '"Lexend", "Segoe UI", Roboto, sans-serif',
                whiteSpace: "pre-wrap",
                textWrap: "wrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: { xs: "250px", sm: "350px", md: "500px" },
              }}
            >
              {domain}
            </Typography>
          </Box>
          <Typography
            variant="h5"
            sx={{
              maxWidth: "800px",
              mx: "auto",
              mt: 4,
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            {description}
          </Typography>
        </Box>

        {/* Device Mockups */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            gap: 4,
          }}
        >
          {/* Laptop Mockup */}

          <Box className="macbook" sx={{ width: { xs: "100%" } }}>
            <Box className="screen">
              <Box
                class="viewport"
                style={{
                  backgroundImage: `url(${images.desktop})`,
                }}
              ></Box>
            </Box>
            <Box className="base"></Box>
            <Box className="notch"></Box>
          </Box>

          {/* Mobile Mockup */}
          <Box
            sx={{
              width: { xs: "100%", sm: "25%" },
              maxWidth: "300px",
              minWidth: "150px",
              mx: { xs: "auto", sm: 0 },
              display: { xs: "none", sm: "block" },
            }}
          >
            <DeviceFrame
              elevation={3}
              sx={{ borderRadius: "24px", overflow: "hidden" }}
              id="mobile-frame"
            >
              <Box>
                <img
                  src={images.mobile}
                  alt="Mobile Screenshot"
                  style={{ width: "100%", borderRadius: "16px" }}
                />
              </Box>
            </DeviceFrame>
          </Box>
        </Box>

        {/* Footer Section */}
        <Grid
          container
          spacing={4}
          sx={{ mt: { xs: 8, sm: 16 } }}
          textAlign={{ sm: "left", xs: "center" }}
        >
          <Grid
            size={{ xs: 12, sm: 8 }}
            sx={{
              borderLeft: { sm: "1px solid #000" },
              paddingLeft: { sm: 2, xs: 0 },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "medium",
                mb: 2,
              }}
            >
              Generated By
            </Typography>
            {generatedBy.company?.original && (
              <Typography variant="body2">
                {generatedBy.company?.original}
              </Typography>
            )}
            {generatedBy.name?.original && (
              <Typography variant="body2">
                {generatedBy.name?.original}
              </Typography>
            )}
            {generatedBy.email?.original && (
              <Typography variant="caption">
                {generatedBy.email?.original}
              </Typography>
            )}
            {generatedBy.website?.original && (
              <Typography variant="caption" sx={{ display: "block" }}>
                {generatedBy.website?.original}
              </Typography>
            )}
            {generatedBy.phone_number?.original && (
              <Typography variant="caption" sx={{ display: "block" }}>
                {generatedBy.phone_number?.original}
              </Typography>
            )}
          </Grid>
          <Grid
            size={{ xs: 12, sm: 4 }}
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: { sm: "flex-end", xs: "center" },
            }}
          >
            <Box
              sx={{
                width: { xs: "150px", sm: "250px" },
              }}
            >
              <img
                src={MosierLogo}
                alt="Milk Tub Logo"
                style={{ width: "100%", height: "auto" }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </GradientBackground>
  );
}

export default WelcomePage;
